import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';

const ROLE_LABELS: Record<string, { label: string; color: string }> = {
  admin:          { label: 'Admin',          color: '#F97316' },
  coach:          { label: 'Coach',          color: '#22D3EE' },
  player_parent:  { label: 'Player / Parent', color: '#A78BFA' },
  viewer:         { label: 'Fan',            color: '#8892A4' },
  league_manager: { label: 'League Manager', color: '#06B6D4' },
};

const card: React.CSSProperties = {
  background: 'var(--surface)', border: '1px solid var(--hairline)',
  borderRadius: 16, padding: '28px 24px', marginBottom: 20,
  boxShadow: '0 1px 6px rgba(0,0,0,0.04)',
};

const sectionTitle: React.CSSProperties = {
  fontSize: 16, fontWeight: 800, color: 'var(--ink-1)',
  marginBottom: 18, paddingBottom: 12,
  borderBottom: '1px solid var(--hairline)',
};

const label: React.CSSProperties = {
  fontSize: 12, fontWeight: 700, color: 'var(--ink-4)',
  textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 5,
};

const value: React.CSSProperties = {
  fontSize: 15, fontWeight: 600, color: 'var(--ink-1)',
};

export default function Account() {
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();

  const [displayName, setDisplayName]   = useState(user?.username || '');
  const [nameStatus, setNameStatus]     = useState<string | null>(null);
  const [nameSaving, setNameSaving]     = useState(false);

  const [currentPw, setCurrentPw]   = useState('');
  const [newPw, setNewPw]           = useState('');
  const [confirmPw, setConfirmPw]   = useState('');
  const [pwStatus, setPwStatus]     = useState<string | null>(null);
  const [pwError, setPwError]       = useState<string | null>(null);
  const [pwSaving, setPwSaving]     = useState(false);

  // Delete account
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword]       = useState('');
  const [deleteError, setDeleteError]             = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading]         = useState(false);
  const deletePwRef = useRef<HTMLInputElement>(null);

  const handleDeleteAccount = async () => {
    setDeleteError(null);
    const pw = deletePwRef.current?.value || deletePassword;
    if (!pw) { setDeleteError('Please enter your password to confirm.'); return; }
    setDeleteLoading(true);
    try {
      await api.delete('/auth/account', { data: { password: pw } });
      logout();
      navigate('/login');
    } catch (err: any) {
      setDeleteError(err.response?.data?.error || 'Failed to delete account. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const roleInfo = ROLE_LABELS[user?.role || 'viewer'] ?? ROLE_LABELS.viewer;

  const saveName = async () => {
    if (!displayName.trim() || displayName.trim() === user?.username) return;
    setNameSaving(true); setNameStatus(null);
    try {
      const { data } = await api.put('/auth/account', { username: displayName.trim() });
      login(data.token, data.user);
      setNameStatus('Display name updated.');
    } catch (err: any) {
      setNameStatus(err.response?.data?.error || 'Failed to update name.');
    } finally { setNameSaving(false); }
  };

  const savePassword = async () => {
    setPwError(null); setPwStatus(null);
    if (!currentPw) { setPwError('Enter your current password.'); return; }
    if (newPw.length < 6) { setPwError('New password must be at least 6 characters.'); return; }
    if (newPw !== confirmPw) { setPwError('New passwords do not match.'); return; }
    setPwSaving(true);
    try {
      const { data } = await api.put('/auth/account', { currentPassword: currentPw, newPassword: newPw });
      login(data.token, data.user);
      setPwStatus('Password changed successfully.');
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
    } catch (err: any) {
      setPwError(err.response?.data?.error || 'Failed to change password.');
    } finally { setPwSaving(false); }
  };

  const handleSignOut = () => { logout(); navigate('/login'); };

  return (
    <div className="container" style={{ maxWidth: 600, paddingTop: 8 }}>
      <h1 style={{ fontSize: '26px', fontWeight: 900, color: 'var(--ink-1)', marginBottom: '6px' }}>Account Settings</h1>
      <p style={{ color: 'var(--ink-3)', fontSize: '14px', marginBottom: '28px' }}>
        Manage your profile, password, and preferences.
      </p>

      {/* ── Profile info ── */}
      <div style={card}>
        <div style={sectionTitle}>Profile</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '24px' }}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: 'linear-gradient(135deg, #F97316, #EA580C)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '26px', fontWeight: 900, color: '#fff', flexShrink: 0,
          }}>
            {user?.username?.charAt(0).toUpperCase()}
          </div>
          <div>
            <div style={{ fontSize: '20px', fontWeight: 800, color: 'var(--ink-1)' }}>{user?.username}</div>
            <div style={{ fontSize: '13px', color: 'var(--ink-3)', marginTop: '2px' }}>{user?.email}</div>
            <div style={{
              display: 'inline-block', marginTop: '6px', padding: '2px 10px',
              borderRadius: '20px', fontSize: '11px', fontWeight: 800,
              letterSpacing: '0.8px', textTransform: 'uppercase',
              background: `${roleInfo.color}22`, color: roleInfo.color,
              border: `1px solid ${roleInfo.color}44`,
            }}>
              {roleInfo.label}
            </div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div>
            <div style={label}>Email</div>
            <div style={value}>{user?.email}</div>
          </div>
          <div>
            <div style={label}>Role</div>
            <div style={{ ...value, color: roleInfo.color }}>{roleInfo.label}</div>
          </div>
        </div>
      </div>

      {/* ── Display name ── */}
      <div style={card}>
        <div style={sectionTitle}>Display Name</div>
        <div className="form-group" style={{ marginBottom: '14px' }}>
          <label style={label}>Name shown across the app</label>
          <input
            type="text" className="form-control"
            value={displayName}
            onChange={e => { setDisplayName(e.target.value); setNameStatus(null); }}
            placeholder="Your display name"
            maxLength={40}
          />
        </div>
        {nameStatus && (
          <div style={{ marginBottom: '12px', fontSize: '13px', fontWeight: 600,
            color: nameStatus.includes('updated') ? '#22C55E' : '#F87171' }}>
            {nameStatus}
          </div>
        )}
        <button
          className="btn btn-primary"
          onClick={saveName}
          disabled={nameSaving || !displayName.trim() || displayName.trim() === user?.username}
        >
          {nameSaving ? 'Saving…' : 'Save Name'}
        </button>
      </div>

      {/* ── Settings (password · sign out · delete) ── */}
      <div style={card}>
        <div style={sectionTitle}>Settings</div>

        {/* Change Password */}
        <div style={{ paddingBottom: '24px', marginBottom: '24px', borderBottom: '1px solid var(--hairline)' }}>
          <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--ink-1)', marginBottom: '14px' }}>Change Password</div>
          <div className="form-group">
            <label style={label}>Current Password</label>
            <input type="password" className="form-control"
              value={currentPw} onChange={e => { setCurrentPw(e.target.value); setPwError(null); }}
              placeholder="Enter current password" autoComplete="current-password" />
          </div>
          <div className="form-group">
            <label style={label}>New Password</label>
            <input type="password" className="form-control"
              value={newPw} onChange={e => { setNewPw(e.target.value); setPwError(null); }}
              placeholder="At least 6 characters" autoComplete="new-password" />
          </div>
          <div className="form-group" style={{ marginBottom: '14px' }}>
            <label style={label}>Confirm New Password</label>
            <input type="password" className="form-control"
              value={confirmPw} onChange={e => { setConfirmPw(e.target.value); setPwError(null); }}
              placeholder="Repeat new password" autoComplete="new-password" />
          </div>
          {pwError && (
            <div style={{ marginBottom: '12px', padding: '10px 14px', borderRadius: '8px',
              background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.25)',
              fontSize: '13px', fontWeight: 600, color: '#F87171' }}>
              {pwError}
            </div>
          )}
          {pwStatus && (
            <div style={{ marginBottom: '12px', padding: '10px 14px', borderRadius: '8px',
              background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.25)',
              fontSize: '13px', fontWeight: 600, color: '#22C55E' }}>
              {pwStatus}
            </div>
          )}
          <button className="btn btn-primary" onClick={savePassword} disabled={pwSaving}>
            {pwSaving ? 'Saving…' : 'Change Password'}
          </button>
        </div>

        {/* Sign Out */}
        <div style={{ paddingBottom: '24px', marginBottom: '24px', borderBottom: '1px solid var(--hairline)' }}>
          <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--ink-1)', marginBottom: '8px' }}>Sign Out</div>
          <p style={{ fontSize: '13px', color: 'var(--ink-3)', marginBottom: '14px' }}>
            Sign out of Slow Rib on this device.
          </p>
          <button
            onClick={handleSignOut}
            style={{
              background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
              color: '#F87171', borderRadius: '10px', padding: '10px 20px',
              fontSize: '14px', fontWeight: 700, cursor: 'pointer',
            }}
          >
            Sign Out
          </button>
        </div>

        {/* Delete Account */}
        <div>
          <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--loss)', marginBottom: '8px' }}>Delete Account</div>
          {!showDeleteConfirm ? (
            <>
              <p style={{ fontSize: '13px', color: 'var(--ink-3)', marginBottom: '14px', lineHeight: '1.6' }}>
                Permanently delete your account and all associated data. This cannot be undone.
              </p>
              <button
                onClick={() => { setShowDeleteConfirm(true); setDeleteError(null); setDeletePassword(''); }}
                style={{
                  background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
                  color: '#F87171', borderRadius: '10px', padding: '10px 20px',
                  fontSize: '14px', fontWeight: 700, cursor: 'pointer',
                }}
              >
                Delete My Account
              </button>
            </>
          ) : (
            <>
              <div style={{
                background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
                borderRadius: '10px', padding: '14px 16px', marginBottom: '16px',
              }}>
                <div style={{ fontSize: '14px', fontWeight: 800, color: '#F87171', marginBottom: '4px' }}>
                  ⚠️ This cannot be undone
                </div>
                <div style={{ fontSize: '13px', color: '#FCA5A5', lineHeight: '1.5' }}>
                  Your account, team memberships, and all associated data will be permanently deleted.
                  Enter your password below to confirm.
                </div>
              </div>
              <div className="form-group" style={{ marginBottom: '14px' }}>
                <label style={label}>Confirm your password</label>
                <input
                  ref={deletePwRef}
                  type="password"
                  className="form-control"
                  value={deletePassword}
                  onChange={e => { setDeletePassword(e.target.value); setDeleteError(null); }}
                  placeholder="Enter your current password"
                  autoComplete="current-password"
                />
              </div>
              {deleteError && (
                <div style={{
                  marginBottom: '14px', padding: '10px 14px', borderRadius: '8px',
                  background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.25)',
                  fontSize: '13px', fontWeight: 600, color: '#F87171',
                }}>
                  {deleteError}
                </div>
              )}
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={handleDeleteAccount}
                  disabled={deleteLoading}
                  style={{
                    background: '#EF4444', border: 'none', color: '#fff',
                    borderRadius: '10px', padding: '10px 20px',
                    fontSize: '14px', fontWeight: 700, cursor: deleteLoading ? 'not-allowed' : 'pointer',
                    opacity: deleteLoading ? 0.7 : 1,
                  }}
                >
                  {deleteLoading ? 'Deleting…' : 'Permanently Delete Account'}
                </button>
                <button
                  onClick={() => { setShowDeleteConfirm(false); setDeleteError(null); setDeletePassword(''); }}
                  disabled={deleteLoading}
                  className="btn btn-ghost"
                >
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
